/* screens/codex-usage.jsx — VD-01 · CodexUsage panel (replaces the $ meters on a codex
   workspace). codex is sub-$0, so the $ surfaces are nonsense there; this replaces them with
   the two quota windows that actually govern a codex build:
     • the rolling 5-HOUR SESSION window (live via PY-21's sidecar)
     • the WEEKLY window — an honest 'pending' state until PY-19 lands the meter
   plus a last-synced stamp. Four states, driven by MAV.setCodexState():
     live · pending-weekly (default) · no-data/404 · limit-hit.
   Reads window.MAV.getCodexUsage(); re-reads on the 'goose-codex-usage' event (the sidecar
   push, LIVE-WIRE per PY-21). Countdowns are FE-derived from resetAt (no streamed ticks).
   Ported from 🛩️ Viper's VD-01 prototype; house tokens + the dashboard stat/card idiom. */

const { useState: useCxuState, useEffect: useCxuEffect } = React;

// a 1s-ticking clock so reset countdowns stay live
function useCxuNow() {
  const [, tick] = useCxuState(0);
  useCxuEffect(() => { const t = setInterval(() => tick(x => x + 1), 1000); return () => clearInterval(t); }, []);
}
// re-read usage whenever a state swap fires (mock setCodexState + future live sidecar pushes)
function useCodexUsage() {
  const [u, setU] = useCxuState(() => window.MAV.getCodexUsage());
  useCxuEffect(() => {
    const h = () => setU(window.MAV.getCodexUsage());
    window.addEventListener('goose-codex-usage', h);
    return () => window.removeEventListener('goose-codex-usage', h);
  }, []);
  return u;
}

function cxuCountdown(ms) {
  if (ms <= 0) return 'now';
  const m = Math.floor(ms / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
  if (d >= 1) return d + 'd ' + (h % 24) + 'h';
  if (h >= 1) return h + 'h ' + (m % 60) + 'm';
  if (m >= 1) return m + 'm';
  return Math.floor(ms / 1000) + 's';
}
function cxuClock(ts) { return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }); }
function cxuAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 90) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return m + ' min ago';
  return Math.floor(m / 60) + 'h ago';
}

/* Compact stat-grid tile — replaces the "Token spend $" tile under codex. */
function CodexSessionTile({ go }) {
  const u = useCodexUsage(); useCxuNow();
  const down = !u || u.sidecar !== 'ok';
  const s = u && u.session, hit = s && s.hit;
  // codex reports % USED; the Codex app shows % REMAINING — render remaining (100 - used) so the
  // tile matches what users read in the app. null usedPct (sidecar-ok-but-no-%) stays '—'.
  const left = s && typeof s.usedPct === 'number' ? 100 - s.usedPct : null;
  const hasPct = !down && left != null;
  return (
    <div className="stat clickable" title="Codex 5-hour session quota — % remaining · click to refresh" onClick={() => { if (window.MAV && window.MAV.refreshCodexUsage) window.MAV.refreshCodexUsage(); go && go('m_spend'); }} data-stat="codex-session">
      <span className="stat-go"><I.arrow size={16} /></span>
      <div className="k"><I.jet size={14} /> Codex session</div>
      {hasPct ? <div className="v">{left}<small>% left</small></div> : <div className="v cxu-dim">—</div>}
      <div className={'meter' + (hit ? ' warn' : '') + (hasPct ? '' : ' meter-dim')} style={{ marginTop: 12 }}>
        <span style={{ width: (hasPct ? left : 0) + '%' }}></span>
      </div>
      <div className="sub" style={{ marginTop: 8 }}>
        {down ? 'subscription active · no snapshot yet' : hit ? 'limit reached' : s ? 'resets in ' + cxuCountdown(s.resetAt - Date.now()) : ''}
      </div>
    </div>
  );
}

/* The panel — replaces the "Cost by stage" card under codex. */
function CodexUsagePanel() {
  const u = useCodexUsage(); useCxuNow();
  // Always render — a codex workspace should always show this panel. No usage yet → a graceful
  // default, NOT an error (u is null before the first sidecar read, or u.sidecar !== 'ok').
  const down = !u || u.sidecar !== 'ok';
  const s = u && u.session, w = u && u.weekly, hit = s && s.hit;
  const sLeft = s && typeof s.usedPct === 'number' ? 100 - s.usedPct : null;
  const wLeft = w && typeof w.usedPct === 'number' ? 100 - w.usedPct : null;
  return (
    <div className="card card-pad cxu" data-codex-usage={down ? 'down' : u.state}>
      <div className="sec-head">
        <span className="sec-title">Codex usage</span>
        <span className="cxu-engine"><I.jet size={12} /> codex</span>
      </div>

      {down ? (
        <div className="cxu-nodata">
          <span className="cxu-nodata-ico"><I.jet size={20} /></span>
          <div>
            <div className="cxu-nodata-t">Codex subscription active</div>
            <div className="cxu-nodata-d">No usage snapshot yet — the session and weekly meters appear once codex reports quota. Builds are unaffected.</div>
          </div>
        </div>
      ) : (
        <React.Fragment>
          {/* 5-hour session */}
          <div className={'cxu-block' + (hit ? ' hit' : '')}>
            <div className="cxu-row">
              <span className="cxu-k"><I.clock size={13} /> 5-hour session</span>
              <span className="cxu-val">{sLeft == null ? '—' : sLeft}<small>% left</small></span>
            </div>
            <div className={'meter' + (hit ? ' warn' : '')}><span style={{ width: (sLeft == null ? 0 : sLeft) + '%' }}></span></div>
            <div className={'cxu-sub' + (hit ? ' cxu-hit' : '')}>
              {hit
                ? <React.Fragment><I.flame size={12} /> Session limit reached · auto-resumes {cxuClock(s.resetAt)}</React.Fragment>
                : <React.Fragment>Resets in {cxuCountdown(s.resetAt - Date.now())} · {cxuClock(s.resetAt)}</React.Fragment>}
            </div>
          </div>

          {/* weekly — honest 'pending' until PY-19 */}
          <div className="cxu-block">
            <div className="cxu-row">
              <span className="cxu-k"><I.calendar size={13} /> Weekly</span>
              {w.state === 'pending'
                ? <span className="cxu-pending">pending</span>
                : <span className="cxu-val">{wLeft == null ? '—' : wLeft}<small>% left</small></span>}
            </div>
            {w.state === 'pending'
              ? <div className="meter meter-pending"><span></span></div>
              : <div className="meter"><span style={{ width: (wLeft == null ? 0 : wLeft) + '%' }}></span></div>}
            <div className="cxu-sub muted">
              {w.state === 'pending' ? 'Weekly metering ships with PY-19' : 'Resets in ' + cxuCountdown(w.resetAt - Date.now())}
            </div>
          </div>
        </React.Fragment>
      )}

      <div className="cxu-foot">
        <span className={'cxu-dot' + (down ? ' off' : '')}></span>
        {down ? (u ? 'Last synced ' + cxuAgo(u.capturedAt) : 'Awaiting first snapshot') : 'Synced ' + cxuAgo(u.capturedAt) + ' · live'}
      </div>
    </div>
  );
}

Object.assign(window, { CodexUsagePanel, CodexSessionTile });
